import { G2 } from '@antv/g2plot';
import { UserContext } from 'contexts/ConfigContext';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
import { isEqual } from 'lodash';
import { FC, memo, useContext, useEffect } from 'react';
import { formatNumWithKMB, getConfig, removeFunctionFromProps } from 'utils/helperMethods';

type StackedDotswithBarsProps = {
    containerName?: string;
    dataSet: any[];
    height?: number;
    width?: number;
    xField: string;
    pointsField: string;
    pointMin?: number;
    pointMax?: number;
    pointColorField: string;
    pointsColorMap?: Record<string, string>;
    barField: string;
    barMin?: number;
    barMax?: number;
    y2Type?: 'point' | 'line';
    loading?: boolean;
    isPointPercentKPI?: boolean;
    xLabelFontSize?: number;
    pointTickCount?: number;
};
const StackedDotswithBars: FC<StackedDotswithBarsProps> = ({
    containerName = 'stacked-dots-with-bar',
    dataSet = [],
    height = 200,
    width,
    xField = '',
    pointsField = '',
    pointMin = 0,
    pointMax = 10000,
    pointColorField = '',
    pointsColorMap = {},
    barField = '',
    barMin = 0,
    barMax = 20000,
    y2Type = 'point',
    loading = false,
    isPointPercentKPI = false,
    xLabelFontSize,
    pointTickCount,
}) => {
    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontFamily } = commonSetting;
    useEffect(() => {
        if (!window) return;
        const dataByXKey = (dataSet || []).reduce<Record<string, any[]>>((acc, row) => {
            const key = String(row?.[xField] ?? '');
            if (!acc[key]) acc[key] = [];
            acc[key].push(row);
            return acc;
        }, {});

        const chart = new G2.Chart({
            container: containerName,
            autoFit: width ? false : true,
            height: height,
            width: width,
        });
        chart.data(dataSet);
        chart.interaction('active-region');

        chart.axis(xField, {
            label: {
                autoHide: false,
                autoEllipsis: xLabelFontSize ? true : false,
                style: {
                    fontSize: xLabelFontSize ? xLabelFontSize : graphFontSize,
                    fill: graphLabelColor,
                    fontFamily: graphFontFamily,
                },
            },
            tickLine: {
                alignTick: true,
            },
        });

        // Points config - use nice: true with explicit tickCount for alignment with left Y-axis
        chart.scale(pointsField, {
            nice: true,
            min: pointMin,
            max: pointMax,
            ...(pointTickCount && { tickCount: pointTickCount }),
        });
        // Hide axis for points (margin %) - no grid lines
        chart.axis(pointsField, { label: null, grid: null });

        // Bar config - use nice: false to ensure exact min/max bounds for alignment with Y-axes
        chart.scale(barField, { nice: false, min: barMin, max: barMax });
        // Show grid lines for bars (volume) to align with right Y-axis
        chart.axis(barField, {
            label: null,
            grid: {
                line: {
                    style: {
                        lineDash: [3, 3],
                        stroke: '#d9d9d9',
                        lineWidth: 1,
                    },
                },
            },
        });
        chart.legend(false);
        chart.tooltip({
            showMarkers: false,
            // show colored markers in tooltip list
            shared: true,
            follow: true,
            customItems: (items: any[]) => {
                const title = items?.[0]?.title;
                if (!title) return items;

                const rows = dataByXKey[String(title)] || [];
                if (!rows.length) return items;

                // Always show the bar value (single series)
                const barRaw = rows?.[0]?.[barField];
                const barItem = {
                    name: barField,
                    value: formatNumWithKMB(barRaw),
                    title,
                    color: '#F6D9A7',
                    marker: {
                        symbol: 'square',
                        style: { fill: '#F6D9A7' },
                    },
                };

                // Always show point values (typically one per tier / pointColorField)
                const seen = new Set<string>();
                const pointItems = rows
                    .map((row: any) => {
                        const name = String(row?.[pointColorField] ?? '');
                        const key = `${name}`;
                        if (!name || seen.has(key)) return null;
                        seen.add(key);
                        const yAxis = row?.[pointsField];
                        const pointColor = pointsColorMap?.[name];
                        return {
                            name,
                            value: isPointPercentKPI ? `${formatNumWithKMB(yAxis)}%` : formatNumWithKMB(yAxis),
                            title,
                            color: pointColor,
                            marker: pointColor
                                ? {
                                      symbol: 'circle',
                                      style: { fill: pointColor },
                                  }
                                : undefined,
                        };
                    })
                    .filter(Boolean);

                return [barItem, ...(pointItems as any[])];
            },
        });
        // Bar
        chart
            .interval()
            .position(`${xField}*${barField}`)
            .color('#F6D9A7')
            .size(40)
            .tooltip(`${xField}*${barField}`, (xAxis: any, yAxis: any) => {
                return {
                    title: xAxis,
                    name: barField,
                    value: formatNumWithKMB(yAxis),
                };
            });

        if (y2Type === 'point') {
            // Points
            chart
                .point()
                .position(`${xField}*${pointsField}`)
                .shape('circle')
                .tooltip(`${xField}*${pointsField}*${pointColorField}`, (xAxis: any, yAxis: any, color: any) => {
                    return {
                        title: xAxis,
                        name: color,
                        value: isPointPercentKPI ? `${formatNumWithKMB(yAxis)}%` : formatNumWithKMB(yAxis),
                    };
                })
                .color(pointColorField, (fieldVal: string) => pointsColorMap[fieldVal]);
        } else {
            // Line
            chart
                .line()
                .position(`${xField}*${pointsField}`)
                .tooltip(`${xField}*${pointsField}*${pointColorField}`, (xAxis: any, yAxis: any, color: any) => {
                    return {
                        title: xAxis,
                        name: color,
                        value: isPointPercentKPI ? `${formatNumWithKMB(yAxis)}%` : formatNumWithKMB(yAxis),
                    };
                })
                .color(pointColorField, (fieldVal: string) => pointsColorMap[fieldVal]);
        }

        // Line

        chart.render();
        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataSet, width, isWindowTabActive]);
    return (
        <div>
            <div id={containerName} style={{ height: `${height}px`, opacity: loading ? 0.3 : 1 }}></div>
        </div>
    );
};
function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps }); //remove function type from props for referential equality
    return isEqual(clonePrev, cloneNext);
}
export default memo(StackedDotswithBars, arePropsEqual);
